import * as React from 'react';
import type { LucideIcon } from 'lucide-react';

interface DashboardPageHeaderProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  /** Optional right-aligned action slot. */
  actions?: React.ReactNode;
}

/**
 * Header bar shared by every dashboard page — soft brand-gradient banner with
 * an icon chip + title + optional description + optional trailing actions.
 * Matches the Module Home banner chrome (see ModuleHomeLayout) so dashboards and
 * module landings read as one consistent surface.
 */
export function DashboardPageHeader({
  icon: Icon,
  title,
  description,
  actions,
}: DashboardPageHeaderProps) {
  return (
    <div className="tw-relative tw-overflow-hidden tw-rounded-2xl tw-border tw-bg-gradient-to-br tw-from-primary/10 tw-via-background tw-to-secondary/10">
      <span
        aria-hidden
        className="tw-pointer-events-none tw-absolute tw--right-24 tw--top-24 tw-h-64 tw-w-64 tw-rounded-full tw-bg-primary/10 tw-blur-3xl"
      />
      <span
        aria-hidden
        className="tw-pointer-events-none tw-absolute tw--bottom-32 tw--left-16 tw-h-64 tw-w-64 tw-rounded-full tw-bg-secondary/10 tw-blur-3xl"
      />
      <div className="tw-relative tw-flex tw-flex-col tw-gap-3 tw-px-5 tw-py-5 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between sm:tw-px-6 sm:tw-py-6">
        <div className="tw-flex tw-items-start tw-gap-4">
          <div className="tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-primary tw-text-primary-foreground tw-shadow-lg tw-ring-4 tw-ring-primary/10">
            <Icon className="tw-h-6 tw-w-6" />
          </div>
          <div>
            <h1 className="tw-text-2xl tw-font-semibold tw-tracking-tight tw-text-[color:var(--vz-heading-color)]">
              {title}
            </h1>
            {description ? (
              <p className="tw-mt-1 tw-text-sm tw-text-muted-foreground">{description}</p>
            ) : null}
          </div>
        </div>
        {actions ? (
          <div className="tw-flex tw-flex-shrink-0 tw-flex-wrap tw-items-center tw-gap-2">
            {actions}
          </div>
        ) : null}
      </div>
    </div>
  );
}
